/**
 * State machine xstate v5 — flow standardisé d'un game run.
 *
 * Source : DEC-005 + architecture.md §3-ter.
 *
 * Phases : intro → demo → calibration_warmup → staircase → wrap_up → debrief
 * Orthogonaux : paused, aborted
 *
 * Persistence : on snapshot machine.snapshot vers IndexedDB pendant la session
 * et vers Postgres staircase_runs.full_replay au wrap_up (anti-cheat).
 */
import { assign, setup } from 'xstate';
const initialContext = (config) => ({
    phase: 'intro',
    resumePhase: null,
    staircase: null,
    trialIdx: 0,
    startedAt: Date.now(),
    pausedAtMs: 0,
    totalPausedMs: 0,
    sessionId: null,
    gameRunId: null,
    errors: [],
    thresholdFinal: null,
});
/** Setup factory — appelée par le host avec une GameShellConfig spécifique. */
export function makeGameShellMachine(config) {
    return setup({
        types: {
            context: {},
            events: {},
            input: {},
        },
        guards: {
            shouldShowDemo: () => config.showDemo,
            shouldShowWarmup: () => config.showCalibrationWarmup,
        },
        actions: {
            setPhase: assign(({ context, event: _event }, params) => ({
                ...context,
                phase: params.phase,
            })),
            bumpTrial: assign(({ context }) => ({
                ...context,
                trialIdx: context.trialIdx + 1,
            })),
            capturePauseStart: assign(({ context }) => ({
                ...context,
                pausedAtMs: Date.now(),
                resumePhase: context.phase,
            })),
            accumulatePauseDuration: assign(({ context }) => ({
                ...context,
                totalPausedMs: context.totalPausedMs + (context.pausedAtMs ? Date.now() - context.pausedAtMs : 0),
                pausedAtMs: 0,
            })),
            recordThreshold: assign(({ context, event }) => {
                if (event.type !== 'STAIRCASE_DONE')
                    return context;
                return { ...context, thresholdFinal: event.threshold };
            }),
            assignSession: assign(({ context, event }) => {
                if (event.type !== 'START')
                    return context;
                return { ...context, sessionId: event.sessionId };
            }),
        },
    }).createMachine({
        id: 'gameShell',
        initial: 'intro',
        context: ({ input }) => initialContext(input?.config ?? config),
        states: {
            intro: {
                entry: { type: 'setPhase', params: { phase: 'intro' } },
                on: {
                    START: [
                        { target: 'demo', guard: 'shouldShowDemo', actions: 'assignSession' },
                        { target: 'calibration_warmup', guard: 'shouldShowWarmup', actions: 'assignSession' },
                        { target: 'staircase', actions: 'assignSession' },
                    ],
                    SKIP_DEMO: { target: 'staircase' },
                    ABORT: { target: 'aborted' },
                },
            },
            demo: {
                entry: { type: 'setPhase', params: { phase: 'demo' } },
                on: {
                    DEMO_DONE: [
                        { target: 'calibration_warmup', guard: 'shouldShowWarmup' },
                        { target: 'staircase' },
                    ],
                    PAUSE: { target: 'paused' },
                    ABORT: { target: 'aborted' },
                },
            },
            calibration_warmup: {
                entry: { type: 'setPhase', params: { phase: 'calibration_warmup' } },
                on: {
                    WARMUP_DONE: { target: 'staircase' },
                    PAUSE: { target: 'paused' },
                    ABORT: { target: 'aborted' },
                },
            },
            staircase: {
                entry: { type: 'setPhase', params: { phase: 'staircase' } },
                on: {
                    TRIAL_COMPLETED: { actions: 'bumpTrial' },
                    STAIRCASE_DONE: { target: 'wrap_up', actions: 'recordThreshold' },
                    PAUSE: { target: 'paused' },
                    ABORT: { target: 'aborted' },
                },
            },
            wrap_up: {
                entry: { type: 'setPhase', params: { phase: 'wrap_up' } },
                on: {
                    WRAP_UP_DONE: { target: 'debrief' },
                    ABORT: { target: 'aborted' },
                },
            },
            debrief: {
                entry: { type: 'setPhase', params: { phase: 'debrief' } },
                on: {
                    COMPLETE: { target: 'completed' },
                },
            },
            paused: {
                entry: [
                    { type: 'setPhase', params: { phase: 'paused' } },
                    'capturePauseStart',
                ],
                on: {
                    RESUME: [
                        // route back to whatever phase we were in
                        { target: 'demo', guard: ({ context }) => context.resumePhase === 'demo' },
                        { target: 'calibration_warmup', guard: ({ context }) => context.resumePhase === 'calibration_warmup' },
                        { target: 'staircase' },
                    ],
                    ABORT: { target: 'aborted' },
                },
                exit: ['accumulatePauseDuration'],
            },
            aborted: {
                entry: { type: 'setPhase', params: { phase: 'aborted' } },
                type: 'final',
            },
            completed: {
                type: 'final',
            },
        },
    });
}
//# sourceMappingURL=machine.js.map